import { alvoImpacto, type TipoAlvoImpacto } from './alvosImpacto'
import { impactosAte, pertenceAoOcupante, type ImpactoMedido } from './impactosMedidos'
import type { TipoCorpo } from './ocupantes'
import type { ModoRetencao } from './retencaoOcupante'
import { colisao, instantesDeContato, type TipoColisao } from './timelineCinematica'
import { type MecanismoImpacto } from './tiposDeImpacto'

export type ImpactoClassificavel = ImpactoMedido

export interface OcupanteClassificavel {
  id: string
  tipo: TipoCorpo
  nome: string
  /** só faz sentido para pessoa; a carga solta nunca tem retenção */
  retencao?: ModoRetencao
}

/**
 * Em que pé está cada etapa NESTE instante da régua.
 *
 * `pendente` é a novidade que o bloco estático não sabia dizer: a etapa ainda não
 * chegou, e não ter contato agora não é o mesmo que não ter contato nenhum.
 * `retido` é a outra: um ocupante de cinto que não bate em nada não é "nada
 * aconteceu" — é o 2º impacto ocorrendo por desaceleração, com a retenção
 * distribuindo a carga em vez de concentrá-la num ponto. É o melhor resultado
 * possível, e a aba precisa dizê-lo com essas palavras.
 */
export type EstadoDaEtapa = 'pendente' | 'declarado' | 'medido' | 'retido' | 'conceitual' | 'naoRegistrado'

/** Um contato externo do veículo (etapa 1), com seu próprio instante. */
export interface ContatoExterno {
  nome: string
  instante: number
  ocorreu: boolean
}

export interface EtapaDaSequenciaDeImpactos {
  ordem: 1 | 2 | 3 | 4
  titulo: string
  estado: EstadoDaEtapa
  mecanismo: MecanismoImpacto
  /** instante em que a etapa passa a valer; nulo quando não tem um */
  instante: number | null
  /** os contatos medidos que compõem esta etapa, já filtrados pelo instante */
  contatos: readonly ImpactoMedido[]
  /**
   * Contatos externos do veículo nesta etapa — vazio nas etapas 2 a 4. No
   * engavetamento a etapa 1 tem DOIS: o segundo contato ainda é "veículo ×
   * objeto", só que de novo, não uma quinta etapa — as quatro são doutrina
   * fixa (CBMMG), não uma contagem de eventos.
   */
  contatosExternos: readonly ContatoExterno[]
  leitura: string
}

const listar = (contatos: readonly ImpactoMedido[]): string =>
  contatos
    .map((c) => `${c.nomeCorpo} → ${c.alvo.toLocaleLowerCase('pt-BR')}`)
    .join('; ')

/**
 * Organiza as leituras da simulação segundo os quatro impactos da aula.
 *
 * `tempo` é obrigatório: as etapas acendem conforme a régua passa por elas, em vez
 * de as quatro ficarem sempre visíveis com o mesmo peso. Sem isso a sequência
 * afirmava, com a régua em zero, contatos que só aconteceriam meio segundo depois.
 */
export function classificarSequenciaDeImpactos(
  tipoColisao: TipoColisao,
  tipoAlvo: TipoAlvoImpacto,
  impactos: readonly ImpactoClassificavel[],
  ocupantes: readonly OcupanteClassificavel[],
  tempo: number,
): EtapaDaSequenciaDeImpactos[] {
  const pessoas = ocupantes.filter((item) => item.tipo === 'pessoa')
  const cargas = ocupantes.filter((item) => item.tipo !== 'pessoa')
  const ate = impactosAte(impactos, tempo)

  const segundo = ate.filter((impacto) =>
    pessoas.some((pessoa) => pertenceAoOcupante(impacto.id, pessoa.id)),
  )
  const quarto = ate.filter(
    (impacto) =>
      cargas.some((carga) => pertenceAoOcupante(impacto.id, carga.id)) &&
      pessoas.some((pessoa) =>
        impacto.alvo.toLocaleLowerCase('pt-BR').includes(pessoa.nome.toLocaleLowerCase('pt-BR')),
      ),
  )

  const instantes = instantesDeContato(tipoColisao)
  const contato = instantes[0]!
  const jaBateu = tempo >= contato
  // com todos de cinto, ausência de contato é a lição, não uma lacuna
  const todosRetidos = pessoas.length > 0 && pessoas.every((p) => p.retencao === 'comCinto')

  const secundario = colisao(tipoColisao).impactoSecundario
  const contatosExternos: ContatoExterno[] = [
    { nome: alvoImpacto(tipoAlvo).nome, instante: contato, ocorreu: jaBateu },
  ]
  if (secundario && instantes[1] != null) {
    contatosExternos.push({
      nome: secundario.nome,
      instante: instantes[1],
      ocorreu: tempo >= instantes[1],
    })
  }
  const leituraContatosExternos =
    contatosExternos.length > 1
      ? contatosExternos
          .map(
            (c, i) =>
              `${i + 1}º contato: ${c.nome.toLocaleLowerCase('pt-BR')} — ` +
              (c.ocorreu ? 'já ocorreu.' : 'ainda não.'),
          )
          .join(' ')
      : jaBateu
        ? `Contato externo do veículo com ${alvoImpacto(tipoAlvo).nome.toLocaleLowerCase('pt-BR')}.`
        : 'Ainda não aconteceu neste instante: o veículo segue em movimento, sem contato externo.'

  return [
    {
      ordem: 1,
      titulo: 'Veículo × objeto ou obstáculo',
      estado: jaBateu ? 'declarado' : 'pendente',
      mecanismo: 'contatoDireto',
      instante: contato,
      contatos: [],
      contatosExternos,
      leitura: leituraContatosExternos,
    },
    {
      ordem: 2,
      titulo: 'Ocupantes × estrutura interna',
      estado:
        segundo.length > 0
          ? 'medido'
          : !jaBateu
            ? 'pendente'
            : todosRetidos
              ? 'retido'
              : 'naoRegistrado',
      mecanismo: segundo.length > 0 ? 'contatoDireto' : 'desaceleracao',
      instante: segundo[0]?.tempo ?? null,
      contatos: segundo,
      contatosExternos: [],
      leitura:
        segundo.length > 0
          ? `${segundo.length} contato(s) de ocupantes medido(s) contra o interior: ${listar(segundo)}.`
          : !jaBateu
            ? 'Ainda não: o ocupante continua acompanhando o veículo, na mesma velocidade dele.'
            : todosRetidos
              ? 'A retenção deteve o corpo antes de ele encontrar o interior. O 2º impacto ' +
                'aconteceu por desaceleração, com a carga distribuída pela fita — e é este o melhor ' +
                'resultado possível, não uma leitura faltando.'
              : 'Nenhum contato de ocupante com energia suficiente foi registrado até aqui.',
    },
    {
      ordem: 3,
      titulo: 'Órgãos internos × cavidades corporais',
      estado: 'conceitual',
      mecanismo: 'conceitual',
      instante: null,
      contatos: [],
      contatosExternos: [],
      leitura:
        'Etapa biomecânica explicada conceitualmente. O simulador não modela órgãos nem diagnostica lesões.',
    },
    {
      ordem: 4,
      titulo: 'Objetos soltos × ocupantes',
      estado: quarto.length > 0 ? 'medido' : cargas.length === 0 ? 'naoRegistrado' : 'pendente',
      mecanismo: 'contatoDireto',
      instante: quarto[0]?.tempo ?? null,
      contatos: quarto,
      contatosExternos: [],
      leitura:
        quarto.length > 0
          ? `${quarto.length} contato(s) de carga solta contra ocupantes medido(s): ${listar(quarto)}.`
          : cargas.length === 0
            ? 'Não há objeto nem animal solto nesta cena. Adicione um para ver o 4º impacto.'
            : 'A carga solta ainda não encontrou nenhum ocupante neste instante.',
    },
  ]
}
